import React, { useState, useEffect } from 'react';
import axiosInstance from 'axiosInstance';
import baseUrl from 'Base_Url/baseUrl';
import moment from 'moment-timezone';
import { useToast } from '@chakra-ui/react';

export default function ClientProfileView() {
  const toast = useToast();
  const Token = localStorage.getItem('token');
  const storedCompanyId = localStorage.getItem('companyId');
  const storedCompanyName = localStorage.getItem('companyName') || 'Polycab india limited Unit-7';

  // Profile data states
  const [userName, setUserName] = useState(
    localStorage.getItem('name') || localStorage.getItem('username') || storedCompanyName
  );
  const [mobileNumber, setMobileNumber] = useState(
    localStorage.getItem('phoneNo') || '8440856276'
  );
  const [email, setEmail] = useState(localStorage.getItem('email') || 'demo@aquasense.tech');
  const [storedDesignation, setStoredDesignation] = useState(localStorage.getItem('designation') || '');
  const [storedUserCreatedAt, setStoredUserCreatedAt] = useState(localStorage.getItem('userCreatedAt') || '');
  const [companyInfo, setCompanyInfo] = useState(null);

  // Edit Profile Modal states (Only name and phoneNo are editable)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', phoneNo: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // 1. Fetch current authenticated user account
  const fetchUserDetails = async () => {
    if (!Token) return;
    try {
      const res = await axiosInstance
        .get(`${baseUrl}/user/get-my-account`, {
          headers: { Authorization: `Bearer ${Token}` },
        })
        .catch(() => null);

      const user = res?.data?.data;
      if (user) {
        if (user.name) {
          setUserName(user.name);
          localStorage.setItem('name', user.name);
        }
        if (user.phoneNo) {
          setMobileNumber(user.phoneNo);
          localStorage.setItem('phoneNo', user.phoneNo);
        }
        if (user.email) {
          setEmail(user.email);
          localStorage.setItem('email', user.email);
        }
        if (user.designation) {
          setStoredDesignation(user.designation);
          localStorage.setItem('designation', user.designation);
        }
        if (user.createdAt) {
          setStoredUserCreatedAt(user.createdAt);
          localStorage.setItem('userCreatedAt', user.createdAt);
        }
      }
    } catch (err) {
      console.warn('User profile fetch error:', err);
    }
  };

  // 2. Fetch company registration & address details
  const fetchCompanyDetails = async () => {
    if (!Token) return;
    try {
      let filter = {};
      if (storedCompanyId && storedCompanyId !== 'default') {
        filter._id = storedCompanyId;
      } else if (storedCompanyName) {
        filter.name = storedCompanyName;
      }

      const res = await axiosInstance
        .get(`${baseUrl}/company?filter=${encodeURIComponent(JSON.stringify(filter))}&limit=1`, {
          headers: { Authorization: `Bearer ${Token}` },
        })
        .catch(() => null);

      const compList = res?.data?.data?.data?.data || res?.data?.data?.data || res?.data?.data;
      if (Array.isArray(compList) && compList.length > 0) {
        setCompanyInfo(compList[0]);
      }
    } catch (err) {
      console.warn('Company profile fetch error:', err);
    }
  };

  useEffect(() => {
    fetchUserDetails();
    fetchCompanyDetails();
  }, [Token, storedCompanyId, storedCompanyName]);

  const handleOpenEditModal = () => {
    setEditForm({
      name: userName || '',
      phoneNo: mobileNumber || '',
    });
    setFormError('');
    setIsEditModalOpen(true);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) {
      setFormError('Please enter your full name');
      return;
    }
    if (!editForm.phoneNo.trim()) {
      setFormError('Please enter your contact phone number');
      return;
    }

    setIsSaving(true);
    setFormError('');

    try {
      const payload = {
        name: editForm.name.trim(),
        phoneNo: editForm.phoneNo.trim(),
      };

      const res = await axiosInstance.post(`${baseUrl}/user/update-my-account`, payload, {
        headers: { Authorization: `Bearer ${Token}` },
      });

      if (res?.data) {
        setUserName(payload.name);
        setMobileNumber(payload.phoneNo);
        localStorage.setItem('name', payload.name);
        localStorage.setItem('phoneNo', payload.phoneNo);

        toast({
          title: 'Profile updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        setIsEditModalOpen(false);
      }
    } catch (err) {
      const errMsg =
        err?.response?.data?.message ||
        err?.response?.data?.data ||
        'Failed to update profile. Please try again.';
      setFormError(errMsg);
      toast({
        title: errMsg,
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const initials = userName
    ? userName
        .split(' ')
        .filter(Boolean)
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : 'U';

  // Format real registered company address
  const formatAddress = (addr) => {
    if (!addr) return '';
    if (typeof addr === 'string') return addr;
    const parts = [];
    if (addr.addressLine) parts.push(addr.addressLine);
    if (addr.city && !addr.addressLine?.includes(addr.city)) parts.push(addr.city);
    if (addr.state && !addr.addressLine?.includes(addr.state)) parts.push(addr.state);
    if (addr.zipcode && !addr.addressLine?.includes(addr.zipcode)) parts.push(addr.zipcode);
    if (addr.country && !addr.addressLine?.includes(addr.country)) parts.push(addr.country);
    return parts.join(', ');
  };

  const companyAddress =
    formatAddress(companyInfo?.address) ||
    'Add - Plot No. 74/1 Paiki 74/1/P, 74/2/1, 74/2/2, 80, Village Vaseti, Taluka: Halol, District: Panchmahals, Gujarat - 389 350, India.';

  const memberSince = (storedUserCreatedAt || companyInfo?.createdAt)
    ? moment(storedUserCreatedAt || companyInfo?.createdAt).format('MMM YYYY')
    : 'Aug 2026';

  const role = storedDesignation || (localStorage.getItem('role') === 'companyUser' ? 'Client Admin' : 'Compliance manager');
  const orgName = companyInfo?.name || storedCompanyName;

  return (
    <section className="client-profile">
      <div className="page-h">
        <div>
          <h1>Profile</h1>
          <p>Account &amp; contact details</p>
        </div>
      </div>

      <div className="row two-eq">
        {/* Left card: Avatar & Role */}
        <div className="card client-card pad" style={{ display: 'flex', gap: '18px', alignItems: 'center' }}>
          <div className="avatar" style={{ width: '64px', height: '64px', fontSize: '22px' }}>
            {initials}
          </div>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 750, color: '#0d3b54' }}>{userName}</div>
            <div style={{ color: 'var(--muted)', fontSize: '13.5px' }}>{role} · {orgName}</div>
            <div className="btnrow" style={{ marginTop: '12px' }}>
              <button
                type="button"
                className="btn primary"
                onClick={handleOpenEditModal}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '5px' }}>
                  <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
                </svg>
                Edit profile
              </button>
            </div>
          </div>
        </div>

        {/* Right card: Contact metadata */}
        <div className="card client-card">
          <div className="card-h">
            <h3>Contact details</h3>
          </div>
          <div className="mdetail" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="md">
              <div className="l">Email</div>
              <div className="v">{email}</div>
            </div>
            <div className="md">
              <div className="l">Mobile number</div>
              <div className="v">{mobileNumber}</div>
            </div>
            <div className="md">
              <div className="l">Organisation</div>
              <div className="v">{orgName}</div>
            </div>
            <div className="md">
              <div className="l">Address</div>
              <div className="v" style={{ wordBreak: 'break-word', lineHeight: 1.4 }}>{companyAddress}</div>
            </div>
            <div className="md">
              <div className="l">Role</div>
              <div className="v">{role}</div>
            </div>
            <div className="md">
              <div className="l">Member since</div>
              <div className="v">{memberSince}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal (Only Name & Phone Number editable) */}
      {isEditModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: 'rgba(11, 46, 69, 0.55)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            animation: 'fade .2s ease',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isSaving) setIsEditModalOpen(false);
          }}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '480px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
              overflow: 'hidden',
              animation: 'slideUp .25s ease',
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '18px 24px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: '#f8fafc',
              }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 750, color: '#0d3b54' }}>
                  Edit Profile
                </h3>
                <p style={{ margin: '3px 0 0', fontSize: '12.5px', color: '#64748b' }}>
                  Update your contact details
                </p>
              </div>
              <button
                type="button"
                onClick={() => !isSaving && setIsEditModalOpen(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '20px',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSaveProfile}>
              <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {formError && (
                  <div
                    style={{
                      background: '#fef2f2',
                      border: '1px solid #fecaca',
                      color: '#b91c1c',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: 600,
                    }}
                  >
                    {formError}
                  </div>
                )}

                {/* 1. Full Name (Editable) */}
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Full Name <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter your full name"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      fontSize: '14px',
                      color: '#0f172a',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                {/* 2. Phone Number (Editable) */}
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Phone Number <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="tel"
                    value={editForm.phoneNo}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, phoneNo: e.target.value }))}
                    placeholder="Enter 10-digit mobile number"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      fontSize: '14px',
                      color: '#0f172a',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>

              {/* Modal Footer */}
              <div
                style={{
                  padding: '14px 24px',
                  borderTop: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '10px',
                  background: '#f8fafc',
                }}
              >
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => setIsEditModalOpen(false)}
                  disabled={isSaving}
                  style={{ padding: '8px 16px', fontSize: '13.5px' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn primary"
                  disabled={isSaving}
                  style={{ padding: '8px 20px', fontSize: '13.5px', minWidth: '120px' }}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
